import { Hymn } from "@/services/hymnsApi";
import { getHymnSlug, scrollToTop } from "@/utils";
import { FavoriteHymn, useFavorites } from "@/utils/export-favorites";
import {
  ChevronDown,
  ChevronRight,
  FolderInput,
  Heart,
  Share2,
  Trash2,
  Volume2,
  X,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClearFavoritesConfirmModal } from "./ClearFavoritesConfirmModal";
import { ExportFavoritesModal } from "./ExportFavoritesModal";
import { ImportFavoritesModal } from "./ImportFavoritesModal";

interface FavoritesWidgetProps {
  currentHymn?: Hymn | null;
  className?: string;
  compact?: boolean;
}

const STORAGE_KEY_FAVORITES_COLLAPSED = "egarizim_favorites_collapsed";

export const FavoritesWidget: React.FC<FavoritesWidgetProps> = ({
  currentHymn,
  className = "",
  compact = false,
}) => {
  const navigate = useNavigate();
  const { favorites, isCurrentFavorited, toggle, remove, clear, maxLimit } =
    useFavorites(currentHymn?.section_code, currentHymn?.number);

  const [isExportOpen, setIsExportOpen] = useState<boolean>(false);
  const [isImportOpen, setIsImportOpen] = useState<boolean>(false);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState<boolean>(false);

  // État replié / déplié persisté dans localStorage (fermé par défaut au démarrage pour gagner de l'espace)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_FAVORITES_COLLAPSED);
        if (saved !== null) {
          return saved === "true";
        }
      } catch (e) {
        console.warn(
          "Failed to read favorites collapsed state from localStorage",
          e,
        );
      }
    }
    return true; // Fermé par défaut au démarrage
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined" && window.localStorage) {
        try {
          localStorage.setItem(STORAGE_KEY_FAVORITES_COLLAPSED, String(next));
        } catch (e) {
          console.warn(
            "Failed to save favorites collapsed state to localStorage",
            e,
          );
        }
      }
      return next;
    });
  };

  const handleOpenSong = (fav: FavoriteHymn) => {
    scrollToTop();
    const slug = getHymnSlug(fav.number, fav.title);
    navigate(
      `/chants-desperance/${encodeURIComponent(fav.section_code)}/${slug}`,
    );
  };

  const handleRemove = (e: React.MouseEvent, fav: FavoriteHymn) => {
    e.stopPropagation();
    remove(fav.section_code, fav.number);
    toast.info(`Cantique N° ${fav.number} retiré des favoris`);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClearConfirmOpen(true);
  };

  const handleConfirmClear = () => {
    clear();
    toast.success("Liste des favoris réinitialisée");
  };

  const getCollectionBadge = (sectionCode: string) => {
    return (
      sectionCode.split("-")[0] || sectionCode.substring(0, 2).toUpperCase()
    );
  };

  if (compact) {
    return (
      <div
        id="favorites-widget-compact"
        className={`rounded-2xl bg-slate-900/90 border border-slate-700/80 shadow-xl backdrop-blur-md transition-all duration-300 ${
          isCollapsed
            ? "p-2.5 sm:p-3"
            : "p-3 sm:p-3.5 h-full flex flex-col justify-between"
        } ${className}`}
      >
        {/* Header Compact Cliquable pour replier / déplier */}
        <div
          className={`flex items-center justify-between gap-2 ${
            isCollapsed ? "" : "pb-2 mb-2 border-b border-slate-800/80"
          }`}
        >
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex items-center gap-2 min-w-0 text-left cursor-pointer group flex-1 py-0.5"
            title={
              isCollapsed
                ? "Afficher les favoris audio"
                : "Masquer les favoris audio"
            }
            aria-expanded={!isCollapsed}
          >
            <div className="w-6 h-6 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 flex-shrink-0 group-hover:scale-105 group-hover:bg-emerald-500/20 transition-all">
              <Heart className="w-3.5 h-3.5 fill-emerald-500/30 text-emerald-400" />
            </div>
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className="text-xs sm:text-sm font-bold text-white tracking-tight truncate group-hover:text-emerald-300 transition-colors">
                Favoris (audio)
              </h3>
              <span className="px-1.5 py-0.2 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-[10px] font-bold font-mono">
                {favorites.length}
              </span>
            </div>
            <div className="text-slate-400 group-hover:text-white transition-colors ml-auto pr-1">
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 ${
                  isCollapsed ? "" : "rotate-180"
                }`}
              />
            </div>
          </button>

          {!isCollapsed && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsImportOpen(true);
                }}
                className="p-1 rounded-lg bg-slate-800/80 hover:bg-sky-950/40 text-slate-400 hover:text-sky-300 text-xs border border-slate-700/80 transition-all flex-shrink-0 cursor-pointer"
                title="Importer des favoris"
                aria-label="Importer des favoris"
              >
                <FolderInput className="w-3 h-3 text-sky-400" />
              </button>
              {favorites.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExportOpen(true);
                    }}
                    className="p-1 rounded-lg bg-slate-800/80 hover:bg-emerald-950/40 text-slate-400 hover:text-emerald-300 text-xs border border-slate-700/80 transition-all flex-shrink-0 cursor-pointer"
                    title="Exporter mes favoris"
                    aria-label="Exporter mes favoris"
                  >
                    <Share2 className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="p-1 rounded-lg bg-slate-800/80 hover:bg-rose-950/40 text-slate-400 hover:text-rose-300 text-xs border border-slate-700/80 transition-all flex-shrink-0 cursor-pointer"
                    title="Vider la liste des favoris"
                    aria-label="Vider la liste des favoris"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Body Compact */}
        {!isCollapsed && (
          <div className="flex-1 overflow-hidden animate-in fade-in duration-200">
            {favorites.length > 0 ? (
              <div className="space-y-1.5 max-h-32 sm:max-h-36 overflow-y-auto pr-1.5 text-left scrollbar-tiny">
                {favorites.map((fav, index) => {
                  const isCurrent =
                    currentHymn &&
                    fav.section_code.toUpperCase() ===
                      currentHymn.section_code.toUpperCase() &&
                    fav.number === currentHymn.number;

                  const badge = getCollectionBadge(fav.section_code);

                  return (
                    <div
                      key={`${fav.section_code}-${fav.number}-${index}`}
                      onClick={() => handleOpenSong(fav)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleOpenSong(fav);
                        }
                      }}
                      className={`group flex items-center justify-between p-2 rounded-lg text-left border transition-all cursor-pointer ${
                        isCurrent
                          ? "bg-amber-950/40 border-amber-500/60 text-amber-200 shadow-md ring-1 ring-amber-400/30"
                          : "bg-slate-950/70 hover:bg-slate-800/90 border-slate-800/80 hover:border-slate-600 text-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0 pr-2">
                        <span className="px-1.5 py-0.5 rounded bg-sky-950/80 border border-sky-500/40 text-sky-300 font-mono text-[9px] sm:text-[10px] font-bold flex-shrink-0">
                          {badge} N° {fav.number}
                        </span>
                        <span
                          className={`text-xs font-semibold truncate ${
                            isCurrent
                              ? "text-amber-200"
                              : "text-slate-200 group-hover:text-white"
                          }`}
                        >
                          {fav.title}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isCurrent && (
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"
                            title="En lecture"
                          />
                        )}
                        {fav.media_url && !isCurrent && (
                          <Volume2 className="w-3 h-3 text-emerald-400" />
                        )}
                        <button
                          onClick={(e) => handleRemove(e, fav)}
                          className="p-1 rounded-md text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors cursor-pointer"
                          title="Retirer des favoris"
                          aria-label={`Retirer ${fav.title} des favoris`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-2.5 px-3 text-center rounded-xl border border-dashed border-slate-800/80">
                <p className="text-[11px] sm:text-xs text-slate-400 mb-2">
                  Aucun favori enregistré
                </p>
                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setIsImportOpen(true)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-sky-500/40 bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 font-semibold text-[10px] sm:text-[11px] transition-all cursor-pointer"
                    title="Importer une liste de cantiques favoris"
                  >
                    <FolderInput className="w-2.5 h-2.5" />
                    <span>Importer</span>
                  </button>
                  {currentHymn && !isCurrentFavorited && (
                    <button
                      disabled={!currentHymn.media_url}
                      onClick={() => {
                        if (!currentHymn.media_url) return;
                        toggle(currentHymn);
                        toast.success(
                          `Cantique N° ${currentHymn.number} ajouté aux favoris !`,
                        );
                      }}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border font-semibold text-[10px] sm:text-[11px] transition-all ${
                        !currentHymn.media_url
                          ? "bg-slate-800/40 border-slate-700/50 text-slate-500 opacity-50 cursor-not-allowed"
                          : "bg-emerald-500/20 hover:bg-emerald-500/30 border-emerald-500/40 text-emerald-300 cursor-pointer"
                      }`}
                      title={
                        !currentHymn.media_url
                          ? "Audio non disponible"
                          : "Ajouter aux favoris"
                      }
                    >
                      <Heart className="w-2.5 h-2.5 fill-current" />
                      <span>Ajouter</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Export Modal */}
        <ExportFavoritesModal
          isOpen={isExportOpen}
          onOpenChange={setIsExportOpen}
          favorites={favorites}
          onOpenImport={() => {
            setIsExportOpen(false);
            setIsImportOpen(true);
          }}
        />

        {/* Import Modal */}
        <ImportFavoritesModal
          isOpen={isImportOpen}
          onOpenChange={setIsImportOpen}
        />

        {/* Clear Confirm Modal */}
        <ClearFavoritesConfirmModal
          isOpen={isClearConfirmOpen}
          onOpenChange={setIsClearConfirmOpen}
          onConfirm={handleConfirmClear}
          count={favorites.length}
        />
      </div>
    );
  }

  return (
    <div
      id="favorites-widget"
      className={`rounded-3xl bg-slate-900/90 border border-slate-800/90 shadow-2xl backdrop-blur-xl overflow-hidden p-6 sm:p-8 transition-all duration-300 ${className}`}
    >
      {/* Widget Header */}
      <div
        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
          isCollapsed ? "" : "pb-5 border-b border-slate-800/80"
        }`}
      >
        <button
          type="button"
          onClick={toggleCollapsed}
          className="flex items-center gap-3 text-left cursor-pointer group flex-1"
          title={
            isCollapsed
              ? "Afficher les favoris audio"
              : "Masquer les favoris audio"
          }
          aria-expanded={!isCollapsed}
        >
          <div className="w-10 h-10 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-inner group-hover:scale-105 group-hover:bg-emerald-500/25 transition-all">
            <Heart className="w-5 h-5 fill-emerald-500/30 text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight group-hover:text-emerald-300 transition-colors">
                Mes Favoris
              </h2>
              <span className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs font-bold font-mono">
                {favorites.length}
              </span>
              <ChevronDown
                className={`w-5 h-5 text-slate-400 group-hover:text-white transition-transform duration-300 ${
                  isCollapsed ? "" : "rotate-180"
                }`}
              />
            </div>
          </div>
        </button>

        {!isCollapsed && (
          <div className="flex items-center gap-2 self-start sm:self-center">
            <button
              type="button"
              onClick={() => setIsImportOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-sky-950/40 text-slate-300 hover:text-sky-300 text-xs font-semibold border border-slate-700/80 hover:border-sky-500/40 transition-all cursor-pointer shadow-sm"
              title="Importer des favoris"
            >
              <FolderInput className="w-3.5 h-3.5 text-sky-400" />
              <span>Importer</span>
            </button>

            {favorites.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setIsExportOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-emerald-950/40 text-slate-300 hover:text-emerald-300 text-xs font-semibold border border-slate-700/80 hover:border-emerald-500/40 transition-all cursor-pointer shadow-sm"
                  title="Exporter mes favoris (fichier ou e-mail)"
                >
                  <Share2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Exporter</span>
                </button>

                <button
                  type="button"
                  onClick={handleClearAll}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-rose-950/40 text-slate-400 hover:text-rose-300 text-xs font-semibold border border-slate-700/80 hover:border-rose-500/40 transition-all cursor-pointer"
                  title="Effacer tous les favoris"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Vider la liste</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Widget Body */}
      {!isCollapsed && (
        <div className="pt-5 animate-in fade-in duration-200">
          {favorites.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {favorites.map((fav, index) => {
                const isCurrent =
                  currentHymn &&
                  fav.section_code.toUpperCase() ===
                    currentHymn.section_code.toUpperCase() &&
                  fav.number === currentHymn.number;

                const badge = getCollectionBadge(fav.section_code);

                return (
                  <div
                    key={`${fav.section_code}-${fav.number}-${index}`}
                    onClick={() => handleOpenSong(fav)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleOpenSong(fav);
                      }
                    }}
                    className={`group relative flex flex-col justify-between p-4 rounded-2xl transition-all duration-200 cursor-pointer overflow-hidden text-left border ${
                      isCurrent
                        ? "bg-amber-950/25 border-amber-500/60 shadow-lg shadow-amber-500/10 ring-1 ring-amber-400/40"
                        : "bg-slate-950/70 hover:bg-slate-800/80 border-slate-800 hover:border-slate-600 shadow-md hover:shadow-xl hover:-translate-y-0.5"
                    }`}
                  >
                    {/* Top Bar: Badges + Remove */}
                    <div className="flex items-center justify-between gap-2 mb-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="px-2 py-0.5 rounded-md bg-sky-950/80 border border-sky-500/40 text-sky-300 font-mono text-[11px] font-bold">
                          {badge}
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-amber-950/70 border border-amber-500/40 text-amber-300 text-[11px] font-black">
                          N° {fav.number}
                        </span>
                        {fav.media_url && (
                          <span
                            className="p-1 rounded-md bg-slate-800/80 text-emerald-400"
                            title="Audio disponible"
                          >
                            <Volume2 className="w-3 h-3" />
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        {isCurrent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold uppercase tracking-wider">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping" />
                            En lecture
                          </span>
                        )}

                        <button
                          onClick={(e) => handleRemove(e, fav)}
                          className="p-1 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 opacity-70 group-hover:opacity-100 transition-all cursor-pointer"
                          title="Retirer des favoris"
                          aria-label={`Retirer ${fav.title} des favoris`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Title & Section */}
                    <div>
                      <h3
                        className={`text-sm font-bold line-clamp-2 leading-snug transition-colors ${
                          isCurrent
                            ? "text-amber-200"
                            : "text-slate-100 group-hover:text-white"
                        }`}
                      >
                        {fav.title}
                      </h3>
                      <p className="text-[11px] text-slate-400 mt-1 line-clamp-1">
                        {fav.section || fav.section_code}
                      </p>
                    </div>

                    {/* Bottom mini indicator */}
                    <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-800/60 text-[11px] text-slate-500">
                      <span className="font-mono text-[10px]">
                        Rang N° {index + 1}
                      </span>
                      <span className="flex items-center gap-0.5 text-slate-400 group-hover:text-amber-300 font-medium transition-colors">
                        <span>Ouvrir</span>
                        <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty State */
            <div className="py-10 px-6 text-center rounded-2xl bg-slate-950/40 border border-dashed border-slate-800">
              <div className="w-12 h-12 rounded-2xl bg-slate-800/80 border border-slate-700/80 flex items-center justify-center mx-auto mb-3 text-slate-400">
                <Heart className="w-6 h-6 text-slate-500" />
              </div>
              <h3 className="text-base font-bold text-slate-200 mb-1">
                Aucun cantique dans vos favoris
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 max-w-md mx-auto mb-4">
                Cliquez sur le bouton « Favori » en haut de la page pour garder
                à portée de main ou importez une liste existante.
              </p>

              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setIsImportOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/40 text-sky-300 font-bold text-xs shadow-md transition-all hover:scale-105 cursor-pointer"
                  title="Importer des cantiques favoris"
                >
                  <FolderInput className="w-4 h-4 text-sky-400" />
                  <span>Importer des favoris</span>
                </button>

                {currentHymn && !isCurrentFavorited && (
                  <button
                    disabled={!currentHymn.media_url}
                    onClick={() => {
                      if (!currentHymn.media_url) return;
                      toggle(currentHymn);
                      toast.success(
                        `Cantique N° ${currentHymn.number} ajouté aux favoris !`,
                      );
                    }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-xs shadow-lg transition-all ${
                      !currentHymn.media_url
                        ? "bg-slate-800 border border-slate-700 text-slate-500 opacity-50 cursor-not-allowed"
                        : "bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-emerald-500/20 hover:scale-105 cursor-pointer"
                    }`}
                    title={
                      !currentHymn.media_url ? "Audio non disponible" : undefined
                    }
                  >
                    <Heart className="w-4 h-4 fill-current" />
                    <span>Ajouter ce chant (N° {currentHymn.number})</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Export Modal */}
      <ExportFavoritesModal
        isOpen={isExportOpen}
        onOpenChange={setIsExportOpen}
        favorites={favorites}
        onOpenImport={() => {
          setIsExportOpen(false);
          setIsImportOpen(true);
        }}
      />

      {/* Import Modal */}
      <ImportFavoritesModal
        isOpen={isImportOpen}
        onOpenChange={setIsImportOpen}
      />

      {/* Clear Confirm Modal */}
      <ClearFavoritesConfirmModal
        isOpen={isClearConfirmOpen}
        onOpenChange={setIsClearConfirmOpen}
        onConfirm={handleConfirmClear}
        count={favorites.length}
      />
    </div>
  );
};
